import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const PasswordResetRequest = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      const result = await resetPassword(email);
      
      if (result.error) {
        setError(result.error.message);
      } else {
        // Show success message immediately
        setMessage('Password reset email sent! Check your inbox for instructions.');
        // Clear the form
        setEmail('');
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/auth/login');
  };

  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-[#F8F8F8] px-4 pb-24">
      <div className="w-full max-w-md">
        {/* Header/Hero Section - Updated to match main page */}
        <header className="bg-primary shadow-sm mb-8 rounded-lg">
          <div className="px-6 py-8 text-center">
            <div className="flex flex-col items-center space-y-2">
              <div className="flex items-center gap-3">
                {/* Tennis ball icon */}
                <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  {/* Base circle with gradient */}
                  <circle cx="12" cy="12" r="11" fill="url(#tennis-gradient)" />
                  
                  {/* Tennis ball seam */}
                  <path
                    d="M12 1C5.925 1 1 5.925 1 12s4.925 11 11 11 11-4.925 11-11S18.075 1 12 1zm0 2c4.97 0 9 4.03 9 9s-4.03 9-9 9-9-4.03-9-9 4.03-9 9-9z"
                    fill="#E5B210"
                    fillOpacity="0.6"
                  />
                  
                  {/* S-curve seam */}
                  <path
                    d="M7 6.5C9.5 9 14.5 7 17 4.5M7 17.5c2.5-2.5 7.5-0.5 10 2"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  
                  {/* Gradient definition */}
                  <defs>
                    <linearGradient id="tennis-gradient" x1="12" y1="1" x2="12" y2="23" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FFB802" />
                      <stop offset="1" stopColor="#E5B210" />
                    </linearGradient>
                  </defs>
                </svg>
                <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-white">
                  Tennis<span className="text-accent">Now</span><span className="text-white">App.com</span>
                </h1>
              </div>
              <p className="text-white/80 text-sm md:text-base font-light">Connecting the Triangle's Tennis Community</p>
            </div>
          </div>
        </header>

        <section className="w-full max-w-md bg-white shadow-xl rounded-2xl p-10">
          <div className="flex items-center mb-6">
            <button
              onClick={handleBack}
              className="flex items-center text-primary hover:text-primaryDark transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Sign In
            </button>
          </div>

          <h2 className="text-2xl font-bold mb-2 text-center text-textDark">
            Reset Your Password
          </h2>
          
          <p className="text-center text-gray-600 mb-8">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {/* Success Message */}
          {message && (
            <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-lg text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-green-800 mb-2">Check Your Email!</h3>
              <p className="text-green-700 text-sm mb-4">{message}</p>
              <p className="text-green-600 text-xs">
                Didn't receive the email? Check your spam folder or try again.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-md border border-gray-300 
                         focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="you@example.com"
                disabled={isSubmitting}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary hover:bg-primaryDark text-white font-semibold 
                       rounded-lg py-3 mt-8 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending Reset Email...
                </div>
              ) : (
                'Send Reset Email'
              )}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Remember your password?{' '}
              <button
                onClick={handleBack}
                className="text-primary font-medium hover:underline"
              >
                Sign in here
              </button>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default PasswordResetRequest; 